import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JhiEventManager, JhiLanguageService } from '../shared/core-utils';
import { DesignCrossesContext } from './design-crosses.context';
import { AlertService } from '../shared/alert/alert.service';
import { Principal } from '../shared';
import { TranslateService } from '@ngx-translate/core';
import { PreviewCrossesModel } from '../shared/study/model/preview-crosses.model';
import { ColumnHeadersEnum } from '../shared/study/model/column-header.enum';
import { SORT_PREDICATE_NONE } from '../germplasm-manager/germplasm-search-resolve-paging-params';
import { GermplasmDetailsPopupService } from '../germplasm-details/germplasm-details-popup.service';
import { ParentModel } from '../shared/study/model/parent.model';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { SubscriptionsManager } from '../shared/util/subscriptions-manager';
import { MAX_NAME_DISPLAY_SIZE } from '../app.constants';

@Component({
    selector: 'jhi-preview-crosses',
    templateUrl: './preview-crosses.component.html',
    styleUrls: ['./preview-crosses.component.scss']
})
export class PreviewCrossesComponent implements OnInit, OnDestroy {

    MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    totalItems = 0;
    crosses: PreviewCrossesModel[] = [];
    originalCrossesList: PreviewCrossesModel[] = [];
    selectedItems: Map<number, PreviewCrossesModel> = new Map<number, PreviewCrossesModel>();
    lastClickIndex: any;

    predicate: any;
    reverse: boolean;
    ColumnHeaders = ColumnHeadersEnum;

    // Pagination properties
    currentPage = 1;
    pageSize = 50;
    pageSizeOptions = [50, 75, 100];
    pagedData: any[] = [];

    isSelectAll: boolean;

    private currentPopover: NgbPopover;

    private subscriptionsManager: SubscriptionsManager = new SubscriptionsManager();

    constructor(private activatedRoute: ActivatedRoute,
                private jhiLanguageService: JhiLanguageService,
                public designCrossesContext: DesignCrossesContext,
                private eventManager: JhiEventManager,
                private router: Router,
                private alertService: AlertService,
                public principal: Principal,
                public translateService: TranslateService,
                public germplasmDetailsPopupService: GermplasmDetailsPopupService
    ) {
    }

    ngOnDestroy(): void {
        this.subscriptionsManager.unsubscribeAll();
        this.designCrossesContext.crosses = [];
        this.crosses = [];
        this.originalCrossesList = [];
        this.selectedItems.clear();
        this.currentPopover = null;
    }

    async ngOnInit() {
        this.subscriptionsManager.add = this.eventManager.subscribe('addToCrosses', (event) => {
            this.crosses = this.designCrossesContext.crosses;
            this.originalCrossesList = [...this.crosses];
            this.totalItems = this.crosses.length;
            this.updatePagedData();
            this.clearSort();
        });

        this.clearSort();
    }

    trackId(index: number, item: PreviewCrossesModel) {
        return item.entryNumber;
    }

    isPageSelected() {
        return this.crosses.length && this.crosses.every((crossesModel: PreviewCrossesModel) => Boolean(this.selectedItems.get(crossesModel.entryNumber)));
    }

    onSelectPage() {
        if (this.isPageSelected()) {
            // remove all items
            this.selectedItems.clear();
        } else {
            // check remaining items
            this.crosses.forEach((crossesModel: PreviewCrossesModel) => this.selectedItems.set(crossesModel.entryNumber, crossesModel));
        }
        this.isSelectAll = this.isPageSelected();
    }

    clearSort() {
        this.predicate = SORT_PREDICATE_NONE;
        this.reverse = false;
    }

    selectAll(isSelectAll: boolean) {
        this.isSelectAll = isSelectAll;
        if (isSelectAll) {
            this.clearSelectedItems();
            this.onSelectPage();
        } else {
            this.clearSelectedItems();
        }
    }

    isSelected(crossesModel: PreviewCrossesModel) {
        return this.selectedItems.get(crossesModel.entryNumber);
    }

    clearSelectedItems() {
        this.selectedItems.clear();
    }

    toggleSelect($event, index, crossesModel: PreviewCrossesModel, checkbox = false) {
        if (!$event.ctrlKey && !$event.metaKey && !checkbox) {
            this.clearSelectedItems();
        }
        let items;
        if ($event.shiftKey) {
            const max = Math.max(this.lastClickIndex, index) + 1,
                min = Math.min(this.lastClickIndex, index);
            items = this.crosses.slice(min, max);
        } else {
            items = [crossesModel];
            this.lastClickIndex = index;
        }
        const isClickedItemSelected = this.selectedItems.get(crossesModel.entryNumber);
        for (const item of items) {
            if (isClickedItemSelected) {
                this.selectedItems.delete(item.entryNumber);
            } else {
                this.selectedItems.set(item.entryNumber, item);
            }
        }
        this.isSelectAll = this.isPageSelected();
    }

    removeSelected() {
        if (this.selectedItems.size === 0) {
            this.alertService.warning('design-crosses.selection.empty');
            return;
        }
        if (this.isPageSelected()) {
            this.crosses = [];
        } else {
            this.crosses = this.crosses.filter((cross) => !this.selectedItems.get(cross.entryNumber));
        }
        this.selectedItems.clear();

        this.crosses.forEach((cross, index) => {
            cross.entryNumber = index + 1;
        });

        this.designCrossesContext.crosses = this.crosses;
        this.originalCrossesList = [...this.crosses];
        this.originalCrossesList.sort((a, b) => Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1);
        this.totalItems = this.crosses.length;
        this.updatePagedData();
    }

    clearAll() {
        this.crosses = [];
        this.originalCrossesList = [];
        this.selectedItems.clear();
        this.designCrossesContext.crosses = this.crosses;
        this.totalItems = 0;
    }

    getMaleParentsDesignations(crossesModel: PreviewCrossesModel) {
        if (crossesModel.maleParents.length === 1) {
            return crossesModel.maleParents[0].designation;
        } else {
            let desig = '[';
            crossesModel.maleParents.forEach((maleParent, index) => {
                desig = desig + maleParent.designation;
                if (index === crossesModel.maleParents.length - 1) {
                    desig = desig + ']';
                } else {
                    desig = desig + ', ';
                }
            });
            return desig;
        }
    }

    getMaleParentsCrosses(crossesModel: PreviewCrossesModel) {
        if (crossesModel.maleParents.length === 1) {
            return crossesModel.maleParents[0].cross;
        } else {
            let cross = '[';
            crossesModel.maleParents.forEach((maleParent, index) => {
                cross = cross + maleParent.cross;
                if (index === crossesModel.maleParents.length - 1) {
                    cross = cross + ']';
                } else {
                    cross = cross + ', ';
                }
            });
            return cross;
        }
    }

    showRightClickMenu($event, p: NgbPopover) {
        if (this.currentPopover) {
            this.currentPopover.close();
        }
        if (this.crosses && this.crosses.length > 0) {
            $event.preventDefault();
            this.currentPopover = p;
            p.open();
        }
    }

    sort() {
        this.crosses = [...this.originalCrossesList];
        if (this.predicate === this.ColumnHeaders.ENTRY_NO) {
            this.crosses.sort((a, b) => {
                if (this.reverse) {
                    return Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1;
                } else {
                    return Number(a.entryNumber) < Number(b.entryNumber) ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.FEMALE_CROSS) {
            this.crosses.sort((a, b) => {
                if (this.reverse) {
                    return a.femalePedigreeString > b.femalePedigreeString ? 1 : -1;
                } else {
                    return a.femalePedigreeString < b.femalePedigreeString ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.MALE_CROSS) {
            this.crosses.sort((a, b) => {
                if (this.reverse) {
                    return a.malePedigreeString > b.malePedigreeString ? 1 : -1;
                } else {
                    return a.malePedigreeString < b.malePedigreeString ? 1 : -1;
                }
            });
        } else if (this.predicate === this.ColumnHeaders.GERMPLASM_ORIGIN) {
            this.crosses.sort((a, b) => {
                if (this.reverse) {
                    return a.seedSource > b.seedSource ? 1 : -1;
                } else {
                    return a.seedSource < b.seedSource ? 1 : -1;
                }
            });
        }
        this.updatePagedData();
    }

    getNames(list: ParentModel[]) {
        if (list) {
            return list.map((parentModel) => parentModel.designation);
        }
        return [];
    }

    updatePagedData(): void {
        if (this.crosses && this.crosses.length > 0) {
            this.totalItems = this.crosses.length;

            // Calculate starting and ending indices for the current page
            const startIndex = (this.currentPage - 1) * this.pageSize;
            const endIndex = Math.min(startIndex + this.pageSize, this.crosses.length);

            // Slice the data to get the current page data
            this.pagedData = this.crosses.slice(startIndex, endIndex);
        } else {
            this.pagedData = [];
            this.totalItems = 0;
        }
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.updatePagedData();
    }

    onPageSizeChange(event: any): void {
        this.pageSize = parseInt(event.target.value, 10);
        this.currentPage = 1; // Reset to first page when changing page size
        this.updatePagedData();
    }

    protected readonly Math = Math;
}
